import { Head, Link, router } from '@inertiajs/react'
import { useState } from 'react'
import Layout from '../../components/Layout'
import MeetingFilterBar from '../../components/meetings/MeetingFilterBar'
import MeetingPagination from '../../components/meetings/MeetingPagination'
import { User } from '../../types'

interface MeetingSummary {
  id: number
  scheduled_date: string
  status: string
  current_phase: string
  facilitator: User
  team_id: number | null
  team_name: string | null
  started_at: string | null
  ended_at: string | null
  duration_minutes: number | null
  template_name: string | null
  display_name: string | null
  action_items_count: number
  topics_count: number
  announcements_count: number
  rating: number | null
}

interface TeamOption {
  id: number
  name: string
}

interface Filters {
  team_id: string
  status: string
}

interface Props {
  meetings: MeetingSummary[]
  teams: TeamOption[]
  user_team_ids: number[]
  startable_team_ids: number[]
  filters: Filters
  page: number
  total_count: number
  per_page: number
}

function formatDate(dateStr: string) {
  const date = new Date(dateStr + 'T00:00:00')
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function formatTime(dateStr: string | null) {
  if (!dateStr) return null
  const date = new Date(dateStr)
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase()
}

function formatDuration(minutes: number | null) {
  if (!minutes) return '-'
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h > 0 && m > 0) return `${h}h ${m}m`
  if (h > 0) return `${h}h`
  return `${m} min`
}

function statusLabel(status: string) {
  switch (status) {
    case 'in_progress': return 'In Progress'
    case 'scheduled': return 'Scheduled'
    case 'completed': return 'Completed'
    case 'cancelled': return 'Cancelled'
    default: return status
  }
}

export default function MeetingsIndex({ meetings, teams, startable_team_ids, filters, page, total_count, per_page }: Props) {
  const startableTeams = teams.filter(team => startable_team_ids.includes(team.id))
  const [selectedTeamId, setSelectedTeamId] = useState('')
  const startTeamId = startableTeams.find(team => String(team.id) === selectedTeamId)?.id || startableTeams[0]?.id
  const handleStartMeeting = () => {
    if (startTeamId) router.visit(`/teams/${startTeamId}/agendas`)
  }

  const canStartMeeting = startableTeams.length > 0

  const thClass = "px-5 py-3 text-left text-[12px] font-medium text-text-muted uppercase tracking-wider"

  return (
    <Layout>
      <Head title="All Meetings - Pinnacle" />

      <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary font-heading">All Meetings</h1>
          <p className="text-sm text-text-muted mt-1">Organization-wide meeting history</p>
        </div>
        {canStartMeeting && (
          <div className="flex items-center gap-3">
          {startableTeams.length > 1 && (
            <select aria-label="Team for new meeting" value={startTeamId} onChange={event => setSelectedTeamId(event.target.value)} className="border border-stone/30 rounded-lg p-2 text-sm">
              {startableTeams.map(team => <option key={team.id} value={team.id}>{team.name}</option>)}
            </select>
          )}
          <button
            onClick={handleStartMeeting}
            className="bg-pinnacle-red hover:bg-pinnacle-red/90 text-white font-semibold py-2.5 px-5 rounded-lg transition-colors text-sm"
          >
            Start a meeting
          </button>
          </div>
        )}
      </div>

      <MeetingFilterBar
        basePath="/meetings"
        filters={filters}
        teams={teams}
        totalCount={total_count}
      />

      {meetings.length === 0 ? (
        <div className="bg-white rounded-xl shadow-card p-12 text-center">
          <p className="text-text-muted">
            No meetings match the current filters.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-stone/20">
                <th className={`${thClass} pl-6 pr-4 w-[140px]`}>Date</th>
                <th className={thClass}>Meeting</th>
                <th className={thClass}>Team</th>
                <th className={thClass}>Status</th>
                <th className={thClass}>Facilitator</th>
                <th className={`${thClass} text-center`}>Duration</th>
                <th className={`${thClass} text-center`}>Actions</th>
                <th className={`${thClass} text-center`}>Rating</th>
                <th className="w-10 px-2 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone/10">
              {meetings.map(meeting => (
                <tr key={meeting.id} className="hover:bg-[#F5F5F5]/20 transition-colors group">
                  <td className="pl-6 pr-4 py-4">
                    <div className="text-sm font-medium text-text-primary">
                      {formatDate(meeting.scheduled_date)}
                    </div>
                    <div className="text-sm text-text-muted">
                      {formatTime(meeting.started_at) || '-'}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="text-sm font-semibold text-text-primary">
                      {meeting.display_name || meeting.template_name || 'Meeting'}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    {meeting.team_id ? (
                      <Link
                        href={`/teams/${meeting.team_id}/agendas`}
                        className="text-sm text-text-primary hover:text-pinnacle-red transition-colors"
                      >
                        {meeting.team_name || '\u2014'}
                      </Link>
                    ) : (
                      <span className="text-sm text-text-muted">{'\u2014'}</span>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <span className={`inline-block px-2 py-0.5 rounded text-[12px] font-medium uppercase tracking-wide ${
                      meeting.status === 'in_progress' ? 'bg-[#FBA737]/15 text-[#B87A20]' :
                      meeting.status === 'completed' ? 'bg-status-complete/15 text-status-complete' :
                      meeting.status === 'cancelled' ? 'bg-stone/20 text-text-muted' :
                      'bg-charcoal/15 text-text-primary'
                    }`}>
                      {statusLabel(meeting.status)}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-sm text-text-secondary">
                    {meeting.facilitator?.full_name || '\u2014'}
                  </td>
                  <td className="px-5 py-4 text-center">
                    <span className="text-sm font-medium text-text-primary">{formatDuration(meeting.duration_minutes)}</span>
                  </td>
                  <td className="px-5 py-4 text-center">
                    <span className="text-sm text-text-secondary">
                      {meeting.action_items_count > 0 ? `${meeting.action_items_count} item${meeting.action_items_count !== 1 ? 's' : ''}` : '\u2014'}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-center">
                    <span className="text-sm font-semibold text-text-primary">
                      {meeting.rating != null ? meeting.rating.toFixed(1) : '-'}
                    </span>
                  </td>
                  <td className="px-2 py-4 text-right">
                    <Link
                      href={`/meetings/${meeting.id}`}
                      className="text-sm font-medium text-text-primary hover:underline pr-4"
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <MeetingPagination
        basePath="/meetings"
        filters={filters}
        page={page}
        totalCount={total_count}
        perPage={per_page}
      />
    </Layout>
  )
}
